import React, { useRef, useState } from 'react';
import { ModuleBase, Stack, ResponsiveImage, Text, Box, AnimatePresence } from '@/components';
import { SideBySideModuleModel } from './SideBySideModule';
import classNames from 'classnames';
import { fadeIn, fadeInLeft, listingGridMotion } from '@/animations';
import DropdownArrow from '@/assets/caretDownLight.svg';

export const motionImage = {
  variants: {
    inactive: {
      opacity: 0,
      scale: 0.95,
    },
    active: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 50,
      },
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      damping: 10,
      mass: 0.1,
      stiffness: 200,
      type: 'spring',
      duration: 0.1,
    },
  },
};

const motionText = (delay: number) => ({
  variants: {
    inactive: {
      x: 50,
      opacity: 0,
    },
    active: {
      x: 0,
      opacity: 1,
      transition: {
        delay,
        type: 'spring',
        damping: 25,
      },
    },
  },
  exit: {
    ...fadeInLeft.variants.inactive,
    transition: {
      damping: 10,
      mass: 0.1,
      stiffness: 100,
      type: 'spring',
    },
  },
});

const CaseStudyQuote = (data: SideBySideModuleModel) => {
  const {
    props: { alignment, cards },
  } = data;

  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(0);
  const [animation, setAnimation] = useState<object | undefined>(undefined);
  const nextActiveCardIndex = useRef<number>(0);

  const _setActiveCard = (direction: number) => {
    if (!cards || cards.length === 0) return;

    const newActiveCardIndex = (activeCardIndex || 0) + direction;
    if (newActiveCardIndex < 0) {
      nextActiveCardIndex.current = cards.length - 1;
    } else if (newActiveCardIndex > cards.length - 1) {
      nextActiveCardIndex.current = 0;
    } else {
      nextActiveCardIndex.current = newActiveCardIndex;
    }
    setActiveCardIndex(null);
  };

  if (!cards || cards.length === 0) return null;

  const activeCard = activeCardIndex !== null && cards ? cards[activeCardIndex] : null;

  return (
    <ModuleBase
      data={{ ...data, props: { ...data.props, backgroundColour: '#ae2d2a' } }}
      noPadding
      className="CaseStudyQuote overflow-hidden py-10 lg:py-18"
    >
      <div
        className={classNames(
          'container flex flex-col justify-between gap-6 lg:items-stretch lg:gap-0',
          alignment === 'Right' ? 'lg:flex-row-reverse' : 'lg:flex-row',
        )}
      >
        <Stack className="flex w-full flex-grow flex-col justify-between gap-6 lg:w-1/2">
          <div className="flex w-full flex-col gap-6 text-[#f5c9c7] lg:gap-16">
            <div>
              <AnimatePresence onExitComplete={() => setActiveCardIndex(nextActiveCardIndex.current)}>
                {activeCard?.preHeading && (
                  <Text key="preHeading" animation={motionText(0.1)} className="text-style-tag uppercase">
                    {activeCard.preHeading}
                  </Text>
                )}
                {activeCard?.text && (
                  <Text
                    key="text"
                    animation={motionText(0.2)}
                    className={classNames(
                      activeCard.text.length > 300 ? 'lg:text-style-h4 xxl:text-style-h3 xl:text-4xl' : 'text-style-h3',
                      'mt-5 xl:w-[95%]',
                    )}
                    dangerouslySetInnerHTML={{ __html: activeCard.text }}
                  />
                )}
              </AnimatePresence>
            </div>
            <AnimatePresence>
              {activeCard?.title && (
                <Text
                  key="title"
                  animation={motionText(0.3)}
                  className="text-style-h4 mt-5 xl:w-[95%]"
                  dangerouslySetInnerHTML={{ __html: activeCard?.title || '' }}
                />
              )}
            </AnimatePresence>
          </div>
          {cards && cards.length > 1 && (
            <div className="flex items-center justify-end">
              <Box animation={fadeIn} className="flex gap-3">
                <button className="cursor-pointer px-2" onClick={() => _setActiveCard(-1)}>
                  <DropdownArrow className="fill-from-bg rotate-90" />
                </button>
                <button className="cursor-pointer px-2" onClick={() => _setActiveCard(1)}>
                  <DropdownArrow className="fill-from-bg -rotate-90" />
                </button>
              </Box>
            </div>
          )}
        </Stack>
        <Stack
          animation={animation}
          onAnimationComplete={() => setAnimation(listingGridMotion)}
          className="relative w-full flex-grow sm:px-14 lg:w-1/2 lg:px-0"
        >
          <div className={classNames(alignment === 'Right' ? 'lg:pr-18 lg:pl-9' : 'lg:pr-9 lg:pl-18', 'h-full')}>
            <div className="xxl:aspect-[575/575] aspect-[385/267] w-full overflow-hidden lg:aspect-[465/600] xl:aspect-[575/600]">
              <AnimatePresence>
                {activeCard && activeCard.image && (
                  <ResponsiveImage
                    key="image"
                    animation={motionImage}
                    image={activeCard.image}
                    widths={{ xs: 375, sm: 432, md: 520, lg: 402, xl: 576, xxl: 656 }}
                    heights={{ xs: 260, sm: 243, md: 290, lg: 520, xl: 600, xxl: 656 }}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
        </Stack>
      </div>
      <div className="hidden">
        {cards.map((card, index) =>
          card.image ? (
            <ResponsiveImage
              key={index}
              image={card.image}
              widths={{ xs: 375, sm: 432, md: 520, lg: 368, xl: 576, xxl: 656 }}
              heights={{ xs: 260, sm: 243, md: 290, lg: 491, xl: 600, xxl: 656 }}
            />
          ) : null,
        )}
      </div>
    </ModuleBase>
  );
};

export default CaseStudyQuote;
